import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ModeratorChangePassword = ({ open, onClose, email, onPasswordChange, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  useEffect(() => {
    if (open) {
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess(false);
      setLoading(false);
    }
  }, [open]);
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!newPassword || !confirmPassword) {
      setError('Both fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const passwordChangePromise = onPasswordChange(newPassword);

      setSuccess(true);

      setNewPassword('');
      setConfirmPassword('');

      await passwordChangePromise;

      setTimeout(() => {
        if (onSuccess) {
          onSuccess('Password changed successfully!');
        }
        if (onClose) {
          onClose();
        }
      }, 1000);
    } catch (err) {
      setSuccess(false);
      setError(err.message || 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="relative max-h-[90vh] w-[92%] max-w-md overflow-y-auto rounded-2xl bg-white shadow-2xl sm:w-full"
          >
            <div className="p-6 md:p-8">
              <h2 className="mb-4 text-center text-2xl font-bold text-blue-600">
                Change Your Password
              </h2>
              <p className="mb-6 text-center text-gray-600">
                For security, please set a new password for your moderator account.
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    className="w-full rounded-lg border border-gray-300 bg-blue-50/50 px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    minLength={6}
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    className="w-full rounded-lg border border-gray-300 bg-blue-50/50 px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    minLength={6}
                    required
                    disabled={loading}
                  />
                </div>

                {error && (
                  <div className="rounded bg-red-100 px-3 py-2 text-sm text-red-700">{error}</div>
                )}
                {success && (
                  <div className="rounded bg-green-100 px-3 py-2 text-sm text-green-700">
                    ✅ Password changed successfully! Closing...
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                    disabled={loading || success}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || success}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-60"
                  >
                    {loading ? 'Changing...' : success ? 'Success!' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ModeratorChangePassword;
